import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { DECK_STUDY_CARD_QUERY_CONFIG } from "#deck-study-card-query-config";

const SKIPPED_DECK_STUDY_CARDS_CONFIG = {
  ...DECK_STUDY_CARD_QUERY_CONFIG,
  where: { skippedAt: { isNotNull: true } },
} as const;

/**
 * `skipped` deck slice: cards skipped from the new-card flow (`skippedAt`), read
 * as each `user_card` with its `fsrsCard` and all content overlays so
 * consumers can derive the front grapheme, card type, and FSRS schedule without
 * a second read.
 *
 * The query key is registered from the read config so the cache entry refetches
 * as the deck changes.
 */
export function useGetSkippedDeckStudyCardsQueryKey() {
  const [db] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const withTableChangeVersion = useActorTableChangeVersion(
    "userCard",
    SKIPPED_DECK_STUDY_CARDS_CONFIG,
  );

  return () => withTableChangeVersion(["skippedDeckStudyCards", { db }] as const);
}

export const [useSkippedDeckStudyCardsQueryOptions, skippedDeckStudyCardsQueryFn] =
  createUseQueryOptions(useGetSkippedDeckStudyCardsQueryKey, async ({ queryKey: [_, { db }] }) => {
    // Soft-delete filter is injected by the wrapped `actorDb` query proxy.
    // The write-clock freshness check supersedes the old `staleTime: 0` override.
    return db(import.meta.location).query.userCard.findMany(SKIPPED_DECK_STUDY_CARDS_CONFIG);
  });
